import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { dealPose, deckPose, randomLayout } from '@lunary/app-core/deal-layouts'
import type { DealLayout, DealPhase, Pose } from '@lunary/app-core/deal-layouts'

export { DEAL_LAYOUTS, dealPose, deckPose, randomLayout, shuffled } from '@lunary/app-core/deal-layouts'
export type { DealLayout, DealPhase, Pose } from '@lunary/app-core/deal-layouts'

export function poseStyle(p: Pose, animate: boolean): CSSProperties {
  return {
    transform: `translate(${p.x}px, ${p.y}px) rotate(${p.r}deg)`,
    transitionDelay: animate ? `${p.delay ?? 0}ms` : '0ms',
    transitionDuration: animate ? `${p.ms ?? 620}ms` : '0ms',
  }
}

const reducedMotion = (): boolean => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

export function useDeal(n: number, w: number, gap: number, onRedeal?: () => void) {
  const [round, setRound] = useState(0)
  const [layout, setLayout] = useState<DealLayout>(randomLayout)
  const [phase, setPhase] = useState<DealPhase>('deck')
  const timers = useRef<number[]>([])
  const redealRef = useRef(onRedeal)

  useEffect(() => {
    redealRef.current = onRedeal
  }, [onRedeal])

  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, reducedMotion() ? 0 : ms))
  }, [])

  useEffect(() => {
    const list = timers.current
    if (phase === 'deck') later(() => setPhase('shuffle'), 120)
    if (phase === 'shuffle') later(() => setPhase('dealing'), 560)
    if (phase === 'dealing') later(() => setPhase('ready'), 1500)
    if (phase === 'collect') {
      later(() => {
        redealRef.current?.()
        setLayout(randomLayout())
        setRound((r) => r + 1)
        setPhase('shuffle')
      }, 650)
    }
    return () => {
      list.forEach((t) => window.clearTimeout(t))
      timers.current = []
    }
  }, [phase, later])

  const redeal = useCallback(() => setPhase('collect'), [])

  const poseFor = (i: number): Pose => {
    const p = phase === 'dealing' || phase === 'ready' ? dealPose(layout, i, n, w, gap) : deckPose(i, n, w)
    return phase === 'collect' ? { ...p, delay: i * 50, ms: 500 } : p
  }
  const animate = phase !== 'deck'
  const styleFor = (i: number): CSSProperties => ({ ...poseStyle(poseFor(i), animate), zIndex: 10 + i })

  return { round, layout, phase, redeal, poseFor, animate, styleFor, shuffling: phase === 'shuffle', ready: phase === 'ready' }
}
